import { useState, useMemo } from 'react';
import { Leaf, TrendingUp, DollarSign, Zap, Flame, Droplets, ChevronDown, ArrowUpDown, Maximize2 } from 'lucide-react';

// EPA emission factors (approximate)
const EMISSION_FACTORS = {
  electric: 0.855, // lbs CO2 per kWh (US avg grid)
  gas: 11.7,       // lbs CO2 per CCF of natural gas
};

/**
 * Insights Sidebar for the Dashboard
 * Displays key facility insights derived from monthly_stats data.
 * Uses card-dashboard-cyan / card-dashboard-muted-cyan classes
 * and dark: prefixed Tailwind utilities to match the dashboard styling.
 */
export default function InsightsSidebar({ monthlyStats, locations }) {
  const [activeTab, setActiveTab] = useState('carbon');
  const [expandedCarbon, setExpandedCarbon] = useState(null);

  // ---- Derived data ----

  const facilityMap = useMemo(() => {
    const map = {};
    (monthlyStats || []).forEach(stat => {
      if (!map[stat.facility]) map[stat.facility] = [];
      map[stat.facility].push(stat);
    });
    return map;
  }, [monthlyStats]);

  const recentMonths = useMemo(() => {
    const months = [...new Set((monthlyStats || []).map(s => s.month))].sort();
    return months.slice(-2);
  }, [monthlyStats]);

  // Facilities with largest cost increases
  const usageTrends = useMemo(() => {
    if (recentMonths.length < 2) return [];
    const [prevMonth, currMonth] = recentMonths;
    const facilityTrends = [];
    for (const [facility, stats] of Object.entries(facilityMap)) {
      const prevCost = stats.filter(s => s.month === prevMonth).reduce((sum, s) => sum + (s.totalCost || 0), 0);
      const currCost = stats.filter(s => s.month === currMonth).reduce((sum, s) => sum + (s.totalCost || 0), 0);
      if (prevCost > 0) {
        facilityTrends.push({ facility, prevCost, currCost, changePercent: ((currCost - prevCost) / prevCost) * 100 });
      }
    }
    return facilityTrends.sort((a, b) => b.changePercent - a.changePercent).slice(0, 5);
  }, [facilityMap, recentMonths]);

  // Top 5 carbon emitters (with per-utility breakdown)
  const carbonRanking = useMemo(() => {
    const facilityCarbon = {};
    const facilityBreakdown = {};
    (monthlyStats || []).forEach(stat => {
      const factor = EMISSION_FACTORS[stat.utility] || 0;
      const co2Lbs = (stat.totalUsage || 0) * factor;
      facilityCarbon[stat.facility] = (facilityCarbon[stat.facility] || 0) + co2Lbs;
      if (!facilityBreakdown[stat.facility]) facilityBreakdown[stat.facility] = {};
      if (!facilityBreakdown[stat.facility][stat.utility]) {
        facilityBreakdown[stat.facility][stat.utility] = { usage: 0, lbs: 0, unit: stat.unit || (stat.utility === 'electric' ? 'kWh' : 'CCF') };
      }
      facilityBreakdown[stat.facility][stat.utility].usage += stat.totalUsage || 0;
      facilityBreakdown[stat.facility][stat.utility].lbs += co2Lbs;
    });
    return Object.entries(facilityCarbon)
      .map(([facility, lbs]) => ({ facility, lbs, tons: lbs / 2000, breakdown: facilityBreakdown[facility] || {} }))
      .sort((a, b) => b.lbs - a.lbs)
      .slice(0, 5);
  }, [monthlyStats]);

  // Highest cost facilities
  const costRanking = useMemo(() => {
    const facilityCost = {};
    (monthlyStats || []).forEach(stat => {
      facilityCost[stat.facility] = (facilityCost[stat.facility] || 0) + (stat.totalCost || 0);
    });
    return Object.entries(facilityCost)
      .map(([facility, cost]) => ({ facility, cost }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 5);
  }, [monthlyStats]);

  // Cost per sq ft
  const costPerSqFt = useMemo(() => {
    if (!locations || locations.length === 0) return [];
    const facilityCost = {};
    (monthlyStats || []).forEach(stat => {
      facilityCost[stat.facility] = (facilityCost[stat.facility] || 0) + (stat.totalCost || 0);
    });
    const results = [];
    locations.forEach(loc => {
      if (loc.squareFootage && facilityCost[loc.name]) {
        results.push({ facility: loc.name, costPerSqFt: facilityCost[loc.name] / loc.squareFootage, totalCost: facilityCost[loc.name], sqft: loc.squareFootage });
      }
    });
    return results.sort((a, b) => b.costPerSqFt - a.costPerSqFt).slice(0, 5);
  }, [monthlyStats, locations]);

  // Usage spikes (>20% month-over-month)
  const usageSpikes = useMemo(() => {
    if (recentMonths.length < 2) return [];
    const [prevMonth, currMonth] = recentMonths;
    const spikes = [];
    for (const [facility, stats] of Object.entries(facilityMap)) {
      for (const utility of [...new Set(stats.map(s => s.utility))]) {
        const prev = stats.find(s => s.month === prevMonth && s.utility === utility);
        const curr = stats.find(s => s.month === currMonth && s.utility === utility);
        if (prev && curr && prev.totalUsage > 0) {
          const changePercent = ((curr.totalUsage - prev.totalUsage) / prev.totalUsage) * 100;
          if (changePercent > 20) {
            spikes.push({ facility, utility, prevUsage: prev.totalUsage, currUsage: curr.totalUsage, unit: curr.unit || (utility === 'electric' ? 'kWh' : 'CCF'), changePercent });
          }
        }
      }
    }
    return spikes.sort((a, b) => b.changePercent - a.changePercent).slice(0, 6);
  }, [facilityMap, recentMonths]);

  const hasData = (monthlyStats || []).length > 0;

  const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(mo) - 1]} ${y}`;
  };

  const formatCurrency = (v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`);

  const tabIcons = {
    carbon: <Leaf className="h-3.5 w-3.5" />,
    trends: <TrendingUp className="h-3.5 w-3.5" />,
    costs: <DollarSign className="h-3.5 w-3.5" />,
  };

  const tabs = [
    { key: 'carbon', label: 'Carbon' },
    { key: 'trends', label: 'Trends' },
    { key: 'costs', label: 'Costs' },
  ];

  /* ─── Empty state ─── */
  if (!hasData) {
    return (
      <div className="card-dashboard-cyan p-5">
        <h3 className="text-base font-semibold mb-2 text-gray-900 dark:text-white">Facility Insights</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">No usage data available yet. Upload bills to see insights here.</p>
      </div>
    );
  }

  /* ─── Main render ─── */
  return (
    <div className="card-dashboard-cyan overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Facility Insights</h3>
        <p className="text-sm mt-0.5 text-gray-500 dark:text-gray-400">Based on last 12 months of data</p>
      </div>

      {/* Tab bar */}
      <div className="flex mx-5 mb-4 rounded-lg p-0.5 bg-gray-100 dark:bg-[#1a1a2e]">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 text-sm font-medium py-1.5 px-2 rounded-md transition-colors ${
              activeTab === tab.key
                ? 'bg-white dark:bg-cyan-500/20 text-sky-700 dark:text-cyan-300 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <span className="mr-1.5 inline-flex">{tabIcons[tab.key]}</span>{tab.label}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="px-5 pb-5 space-y-4">

        {/* ── TRENDS ── */}
        {activeTab === 'trends' && (
          <>
            {usageSpikes.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-orange-600 dark:text-orange-300">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500/20"><Zap className="h-3 w-3" /></span>
                  Usage Spikes ({formatMonth(recentMonths[1])})
                </h4>
                <div className="space-y-1.5">
                  {usageSpikes.map((spike, i) => (
                    <div key={i} className="card-dashboard-muted-cyan flex items-center justify-between px-3 py-2.5 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium truncate block text-gray-900 dark:text-white">{spike.facility}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                          {spike.utility === 'electric' ? <Zap className="h-3 w-3 text-amber-500" /> : <Flame className="h-3 w-3 text-orange-500" />} {spike.prevUsage.toLocaleString()} → {spike.currUsage.toLocaleString()} {spike.unit}
                        </span>
                      </div>
                      <span className="text-orange-500 dark:text-orange-400 font-bold ml-2 whitespace-nowrap">+{spike.changePercent.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {usageTrends.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-sky-600 dark:text-cyan-300">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-500/20"><ArrowUpDown className="h-3 w-3" /></span>
                  Biggest Cost Changes
                </h4>
                <div className="space-y-1.5">
                  {usageTrends.map((trend, i) => (
                    <div key={i} className="card-dashboard-muted-cyan flex items-center justify-between px-3 py-2.5 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium truncate block text-gray-900 dark:text-white">{trend.facility}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(trend.prevCost)} → {formatCurrency(trend.currCost)}</span>
                      </div>
                      <span className={`font-bold ml-2 whitespace-nowrap ${trend.changePercent > 0 ? 'text-red-500 dark:text-red-400' : 'text-emerald-500 dark:text-emerald-400'}`}>
                        {trend.changePercent > 0 ? '+' : ''}{trend.changePercent.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {usageSpikes.length === 0 && usageTrends.length === 0 && (
              <p className="text-sm text-center py-6 text-gray-400 dark:text-gray-500">Need at least 2 months of data to show trends.</p>
            )}
          </>
        )}

        {/* ── CARBON ── */}
        {activeTab === 'carbon' && (
          <>
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-emerald-600 dark:text-emerald-300">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20"><Leaf className="h-3 w-3" /></span>
                Top 5 CO₂ Emitters
              </h4>
              {carbonRanking.length > 0 ? (
                <div className="space-y-1.5">
                  {carbonRanking.map((item, i) => {
                    const maxLbs = carbonRanking[0]?.lbs || 1;
                    const barWidth = (item.lbs / maxLbs) * 100;
                    const isExpanded = expandedCarbon === item.facility;
                    const breakdownEntries = Object.entries(item.breakdown).sort((a, b) => b[1].lbs - a[1].lbs);
                    return (
                      <div key={i}>
                        <div
                          className="card-dashboard-muted-cyan relative px-3 py-2.5 text-sm overflow-hidden cursor-pointer hover:ring-1 hover:ring-emerald-400/40 dark:hover:ring-emerald-500/30 transition-all"
                          onClick={() => setExpandedCarbon(isExpanded ? null : item.facility)}
                        >
                          <div className="absolute inset-y-0 left-0 rounded-xl bg-emerald-100/60 dark:bg-emerald-500/10 transition-all" style={{ width: `${barWidth}%` }} />
                          <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className={`flex items-center justify-center h-5 w-5 rounded-full text-xs font-bold shrink-0 ${
                                i === 0 ? 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300'
                                : i === 1 ? 'bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-300'
                                : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                              }`}>{i + 1}</span>
                              <span className="font-medium truncate text-gray-900 dark:text-white">{item.facility}</span>
                            </div>
                            <div className="flex items-center gap-1.5 ml-2 shrink-0">
                              <span className="font-bold whitespace-nowrap text-emerald-700 dark:text-emerald-300">
                                {item.tons >= 1 ? `${item.tons.toFixed(1)} tons` : `${item.lbs.toFixed(0)} lbs`}
                              </span>
                              <ChevronDown className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                        </div>
                        {isExpanded && breakdownEntries.length > 0 && (
                          <div className="mt-1 ml-4 space-y-1 animate-in fade-in slide-in-from-top-1 duration-200">
                            {breakdownEntries.map(([utility, data]) => {
                              const pct = item.lbs > 0 ? (data.lbs / item.lbs) * 100 : 0;
                              return (
                                <div key={utility} className="card-dashboard-muted-cyan relative px-3 py-2 text-xs overflow-hidden">
                                  <div className={`absolute inset-y-0 left-0 rounded-xl transition-all ${
                                    utility === 'electric' ? 'bg-amber-100/50 dark:bg-amber-500/10' : utility === 'gas' ? 'bg-blue-100/50 dark:bg-blue-500/10' : 'bg-gray-100/50 dark:bg-gray-500/10'
                                  }`} style={{ width: `${pct}%` }} />
                                  <div className="relative flex items-center justify-between">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className="text-sm inline-flex">{utility === 'electric' ? <Zap className="h-3.5 w-3.5 text-amber-500" /> : utility === 'gas' ? <Flame className="h-3.5 w-3.5 text-orange-500" /> : <Droplets className="h-3.5 w-3.5 text-blue-500" />}</span>
                                      <div className="min-w-0">
                                        <span className="font-medium text-gray-900 dark:text-white capitalize">{utility}</span>
                                        <span className="block text-gray-500 dark:text-gray-400 text-[11px]">
                                          {data.usage.toLocaleString()} {data.unit} × {EMISSION_FACTORS[utility] || 0} lbs/unit
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-right ml-2 shrink-0">
                                      <span className="font-bold text-emerald-700 dark:text-emerald-300 block">
                                        {data.lbs >= 2000 ? `${(data.lbs / 2000).toFixed(1)} tons` : `${data.lbs.toFixed(0)} lbs`}
                                      </span>
                                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{pct.toFixed(0)}%</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-center py-6 text-gray-400 dark:text-gray-500">No usage data available for carbon estimates.</p>
              )}
            </div>

            <div className="text-xs px-3 py-2 rounded-lg bg-emerald-50/50 dark:bg-emerald-500/5 text-gray-400 dark:text-gray-500 border border-emerald-100 dark:border-emerald-500/10">
              Estimates use EPA averages: {EMISSION_FACTORS.electric} lbs CO₂/kWh (electric), {EMISSION_FACTORS.gas} lbs CO₂/CCF (gas).
            </div>
          </>
        )}

        {/* ── COSTS ── */}
        {activeTab === 'costs' && (
          <>
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-sky-600 dark:text-cyan-300">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-500/20"><DollarSign className="h-3 w-3" /></span>
                Highest Spend (12 mo.)
              </h4>
              {costRanking.length > 0 ? (
                <div className="space-y-1.5">
                  {costRanking.map((item, i) => {
                    const maxCost = costRanking[0]?.cost || 1;
                    const barWidth = (item.cost / maxCost) * 100;
                    return (
                      <div key={i} className="card-dashboard-muted-cyan relative px-3 py-2.5 text-sm overflow-hidden">
                        <div className="absolute inset-y-0 left-0 rounded-xl bg-sky-100/60 dark:bg-cyan-500/10 transition-all" style={{ width: `${barWidth}%` }} />
                        <div className="relative flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className={`flex items-center justify-center h-5 w-5 rounded-full text-xs font-bold shrink-0 ${
                              i === 0 ? 'bg-sky-100 text-sky-600 dark:bg-cyan-500/20 dark:text-cyan-300'
                              : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}>{i + 1}</span>
                            <span className="font-medium truncate text-gray-900 dark:text-white">{item.facility}</span>
                          </div>
                          <span className="font-bold ml-2 whitespace-nowrap text-sky-700 dark:text-cyan-300">{formatCurrency(item.cost)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-center py-6 text-gray-400 dark:text-gray-500">No cost data available.</p>
              )}
            </div>

            {costPerSqFt.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-purple-600 dark:text-purple-300">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-500/20"><Maximize2 className="h-3 w-3" /></span>
                  Cost per Sq Ft (12 mo.)
                </h4>
                <div className="space-y-1.5">
                  {costPerSqFt.map((item, i) => (
                    <div key={i} className="card-dashboard-muted-cyan flex items-center justify-between px-3 py-2.5 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium truncate block text-gray-900 dark:text-white">{item.facility}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{item.sqft.toLocaleString()} sq ft · {formatCurrency(item.totalCost)} total</span>
                      </div>
                      <span className="font-bold ml-2 whitespace-nowrap text-purple-700 dark:text-purple-300">${item.costPerSqFt.toFixed(2)}/ft²</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
